// Composables
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/store/modules/auth";
import { useSettingsStore } from "@/store/modules/settings";
import { requestMicPermission } from "@/composables/useMicPermission";
import { isTauri } from "@tauri-apps/api/core";
import vuetify from "@/plugins/vuetify";
import { redirectTarget } from "./redirect";

const isStandalonePwa = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
const routes = [
  {
    path: "/",
    component: () => import("@/views/Landing.vue"),
    name: "landing",
    meta: { requiresAuth: false, skipSubscriptionGate: true },
  },
  {
    path: "/onboarding",
    component: () => import("@/views/onboarding/Onboarding.vue"),
    name: "onboarding",
    meta: { requiresAuth: false },
  },
  {
    path: "/login",
    component: () => import("@/views/Login.vue"),
    name: "login",
    meta: { requiresAuth: false },
  },
  {
    path: "/forgot-password",
    component: () => import("@/views/ForgotPassword.vue"),
    name: "forgotPassword",
    meta: { requiresAuth: false, skipOnboardingGate: true },
  },
  {
    path: "/reset-password",
    component: () => import("@/views/ResetPassword.vue"),
    name: "resetPassword",
    meta: { requiresAuth: false, skipOnboardingGate: true },
  },
  {
    path: "/terms",
    component: () => import("@/views/Terms.vue"),
    name: "terms",
    meta: { requiresAuth: false, skipOnboardingGate: true },
  },
  {
    path: "/app",
    component: () => import("@/layouts/Layout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "/pron",
        component: () => import("@/views/pron/Pronounce.vue"),
        name: "pronounce",
        meta: { requiresAuth: true },
        beforeEnter: async (to, from, next) => {
          // Request microphone permission when entering /pron
          await requestMicPermission();
          next();
        },
      },
      {
        path: "/activities",
        component: () => import("@/views/ActivityHub.vue"),
        name: "activities",
        meta: { requiresAuth: true },
      },
      {
        path: "/mushaf/:activity",
        component: () => import("@/views/mushaf/MushafView.vue"),
        name: "mushaf",
        props: true,
        meta: { requiresAuth: true },
        beforeEnter: async (to, from, next) => {
          // Request microphone permission before entering (tasmi3/tilawa both
          // stream mic audio to the realtime-eval socket), same as /pron.
          await requestMicPermission();
          next();
        },
      },
      {
        path: "/classrooms",
        component: () => import("@/views/Classrooms.vue"),
        name: "ClassRooms",
        meta: { requiresAuth: true },
      },
      {
        path: "/join-classroom",
        component: () => import("@/views/JoinViaQRCode.vue"),
        name: "JoinViaQRCode",
        meta: { requiresAuth: true },
      },
      {
        path: "/aboutus",
        component: () => import("@/views/about/About.vue"),
        name: "AboutUs",
        meta: { requiresAuth: false, skipOnboardingGate: true, skipSubscriptionGate: true, desktopLanding: true },
      },
      {
        path: "/contact",
        component: () => import("@/views/contact/Contact.vue"),
        name: "ContactUs",
        meta: { requiresAuth: false, skipOnboardingGate: true, skipSubscriptionGate: true, desktopLanding: true },
      },
      {
        path: "/introduction",
        component: () => import("@/views/Introduction.vue"),
        name: "introduction",
        meta: { requiresAuth: true },
      },
      {
        path: "/selectsora",
        component: () => import("@/components/quran/SelectSora.vue"),
        name: "selectsora",
        meta: { requiresAuth: true },
      },
      {
        path: "/settings",
        component: () => import("@/views/Settings.vue"),
        name: "Settings",
        meta: { requiresAuth: true },
      },
      {
        path: "/payment",
        component: () => import("@/views/payment/Payment.vue"),
        name: "payment",
        // PaymentCancel returns here post-onboarding; must not loop to /subscription.
        meta: { requiresAuth: true, skipSubscriptionGate: true, desktopLanding: true },
      },
      {
        path: "/subscription",
        component: () => import("@/views/onboarding/SubscriptionGate.vue"),
        name: "subscription",
        meta: { requiresAuth: true, skipSubscriptionGate: true },
      },
      {
        path: "/payment/success",
        component: () => import("@/views/PaymentSuccess.vue"),
        name: "PaymentSuccess",
        meta: { requiresAuth: false, skipOnboardingGate: true, skipSubscriptionGate: true },
      },
      {
        path: "/payment/cancel",
        component: () => import("@/views/PaymentCancel.vue"),
        name: "PaymentCancel",
        meta: { requiresAuth: false, skipOnboardingGate: true, skipSubscriptionGate: true },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Initialize auth on first load
let authInitialized = false;

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();

  if (!authInitialized) {
    await authStore.checkAuth();
    authInitialized = true;
  }

  const isAuthenticated = authStore.authenticated;

  // Authenticated implies onboarding happened, except mid-carousel — OTP
  // verify (slide 5) precedes riwaya/subscription (6-7).
  if (isAuthenticated && !settingsStore.hasOnboarded && to.name !== "onboarding") {
    settingsStore.completeOnboarding();
  }
  const hasOnboarded = settingsStore.hasOnboarded;

  // Onboarding for the Tauri app and the installed PWA on mobile; everyone else gets the landing page.
  const isApp = isTauri() || (isStandalonePwa && vuetify.display.mobile.value);
  if (!hasOnboarded && isApp && to.name !== "onboarding" && !to.meta.skipOnboardingGate) {
    next({ name: "onboarding" });
  }
  else if (to.name === "onboarding" && (hasOnboarded || !isApp)) {
    next({ name: hasOnboarded ? "activities" : "landing" });
  }
  else if (to.name === "landing" && isApp) {
    next({ name: "activities" });
  }
  else if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: "login", query: { redirect: to.fullPath } });
  }
  else if (isAuthenticated && to.name === "login") {
    next(redirectTarget(to.query) ?? { name: "introduction" });
  }
  // Subscription undecided, or a past "remind later" has come due.
  else if (isAuthenticated && hasOnboarded && settingsStore.shouldPromptSubscription && !authStore.user?.is_supporter && !to.meta.skipSubscriptionGate) {
    next({ name: "subscription" });
  }
  else if (
    to.meta.userTypeAllowed &&
    authStore.user?.userType &&
    !to.meta.userTypeAllowed.includes(authStore.user.userType)
  ) {
    next({ name: "activities" });
  } else {
    next();
  }
});

export default router;
